import type { Query } from './apiTypes'
import type { ApiWsClientMessage, WsServerMessage } from './wsTypes'

export interface WsManagerOptions {
  url: string
  onMessage: (msg: WsServerMessage) => void
  onOpen?: () => void
  onClose?: (ev: CloseEvent) => void
  onError?: (e: Event) => void
}

/** App-specific close codes from the API WS auth layer. */
const WS_CLOSE_UNAUTH = 4401
const WS_CLOSE_EXPIRED = 4403

function isAuthFailureClose(code: number): boolean {
  return code === WS_CLOSE_UNAUTH || code === WS_CLOSE_EXPIRED
}

export class WsManager {
  private ws: WebSocket | null = null
  private opts: WsManagerOptions
  private queue: ApiWsClientMessage[] = []
  private reconnectAttempts = 0
  private closedByClient = false

  constructor(opts: WsManagerOptions) {
    this.opts = opts
  }

  connect() {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return
    this.closedByClient = false
    this.ws = new WebSocket(this.opts.url)
    this.ws.onopen = () => {
      this.reconnectAttempts = 0
      this.flush()
      this.opts.onOpen?.()
    }
    this.ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(String(ev.data)) as WsServerMessage
        this.opts.onMessage(msg)
      } catch {
        // ignore parse errors
      }
    }
    this.ws.onclose = (ev) => {
      this.opts.onClose?.(ev)
      if (!this.closedByClient && !isAuthFailureClose(ev.code)) {
        this.scheduleReconnect()
      }
    }
    this.ws.onerror = (e) => {
      this.opts.onError?.(e)
    }
  }

  disconnect() {
    this.closedByClient = true
    try { this.ws?.close() } catch { /* ignore */ }
    this.ws = null
  }

  isOpen() {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN
  }

  sendRaw(msg: ApiWsClientMessage) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.queue.push(msg)
      this.connect()
      return
    }
    this.ws.send(JSON.stringify(msg))
  }

  /** Subscribe with the unified `Query` envelope shared by REST. */
  subscribe(query: Query) {
    this.sendRaw({ type: 'subscribe', query })
  }

  unsubscribe() {
    this.sendRaw({ type: 'unsubscribe' })
  }

  ping() {
    this.sendRaw({ type: 'ping' })
  }

  private flush() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    const q = [...this.queue]
    this.queue = []
    q.forEach((m) => this.ws!.send(JSON.stringify(m)))
  }

  private scheduleReconnect() {
    this.reconnectAttempts += 1
    const backoff = Math.min(30_000, 500 * Math.pow(2, this.reconnectAttempts))
    setTimeout(() => {
      if (this.closedByClient) return
      this.connect()
    }, backoff)
  }
}
